import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Lightbulb as ThoughtsIcon,
  CalendarMonth as DiaryIcon,
  Book as JournalIcon,
  ArrowBack as ArrowBackIcon,
  FolderZip as ZipIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import { getUserThoughts } from '../services/thoughtService';
import { getAllDiaryEntries } from '../services/diaryService';
import { getUserJournals } from '../services/journalService';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

// Simple ZIP creation without external library
// Creates a basic ZIP file structure
const createZipBlob = async (files: { name: string; content: string }[]): Promise<Blob> => {
  // For simplicity, we'll create individual files and let browser handle download
  // In production, you might want to use a library like JSZip
  
  // Create a simple concatenated format with separators
  let content = '';
  files.forEach((file, index) => {
    content += `\n${'='.repeat(80)}\n`;
    content += `FILE: ${file.name}\n`;
    content += `${'='.repeat(80)}\n\n`;
    content += file.content;
    content += '\n';
  });
  
  return new Blob([content], { type: 'text/plain' });
};

// Create HTML content for a single entry
const createHtmlContent = (
  title: string,
  content: string,
  date: string,
  labels: string[],
  type: 'thought' | 'diary' | 'journal'
): string => {
  const typeColors = {
    thought: '#6366F1',
    diary: '#EC4899',
    journal: '#8B5CF6',
  };

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      background: #f9fafb;
    }
    .header {
      border-bottom: 3px solid ${typeColors[type]};
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .type-badge {
      display: inline-block;
      background: ${typeColors[type]};
      color: white;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      text-transform: uppercase;
      margin-bottom: 10px;
    }
    h1 {
      font-size: 28px;
      color: #1f2937;
      margin-bottom: 10px;
    }
    .date {
      color: #6b7280;
      font-size: 14px;
    }
    .labels {
      margin-top: 15px;
    }
    .label {
      display: inline-block;
      background: #e5e7eb;
      color: #374151;
      padding: 4px 10px;
      border-radius: 15px;
      font-size: 12px;
      margin-right: 8px;
      margin-bottom: 8px;
    }
    .content {
      background: white;
      padding: 30px;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      white-space: pre-wrap;
      font-size: 16px;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      color: #9ca3af;
      font-size: 12px;
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="header">
    <span class="type-badge">${type}</span>
    <h1>${title}</h1>
    <div class="date">${date}</div>
    ${labels.length > 0 ? `
    <div class="labels">
      ${labels.map(l => `<span class="label">${l}</span>`).join('')}
    </div>
    ` : ''}
  </div>
  <div class="content">${content}</div>
  <div class="footer">
    Exported from mythoughts.buzz on ${format(new Date(), 'MMMM d, yyyy')}
  </div>
</body>
</html>`;
};

// Create plain text content
const createTextContent = (
  title: string,
  content: string,
  date: string,
  labels: string[],
  type: 'thought' | 'diary' | 'journal'
): string => {
  let text = '';
  text += `${type.toUpperCase()}: ${title}\n`;
  text += `Date: ${date}\n`;
  if (labels.length > 0) {
    text += `Labels: ${labels.join(', ')}\n`;
  }
  text += `${'─'.repeat(50)}\n\n`;
  text += content;
  text += '\n\n';
  return text;
};

const Download: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser } = useAuth();
  const { passkey } = usePasskey();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const downloadAsZip = async (
    type: 'thoughts' | 'diary' | 'journals',
    exportFormat: 'html' | 'text'
  ) => {
    if (!currentUser || !passkey) return;

    setLoading(type);
    setProgress(0);
    setError('');
    setSuccess('');

    try {
      let files: { name: string; content: string }[] = [];
      const extension = exportFormat === 'html' ? 'html' : 'txt';

      if (type === 'thoughts') {
        setProgress(10);
        const thoughts = await getUserThoughts(currentUser.uid, passkey);
        setProgress(50);

        files = thoughts
          .filter(t => t.title !== '🔒 Locked')
          .map((thought, index) => {
            const date = thought.updatedAt?.toDate
              ? format(thought.updatedAt.toDate(), 'yyyy-MM-dd')
              : 'unknown';
            const fileName = `thought_${date}_${index + 1}.${extension}`;
            const content =
              format === 'html'
                ? createHtmlContent(
                    thought.title,
                    thought.content,
                    thought.updatedAt?.toDate
                      ? format(thought.updatedAt.toDate(), 'MMMM d, yyyy h:mm a')
                      : 'Unknown date',
                    thought.labels || [],
                    'thought'
                  )
                : createTextContent(
                    thought.title,
                    thought.content,
                    thought.updatedAt?.toDate
                      ? format(thought.updatedAt.toDate(), 'MMMM d, yyyy h:mm a')
                      : 'Unknown date',
                    thought.labels || [],
                    'thought'
                  );
            return { name: fileName, content };
          });
      } else if (type === 'diary') {
        setProgress(10);
        const entries = await getAllDiaryEntries(currentUser.uid, passkey);
        setProgress(50);

        files = entries
          .filter(e => !e.isLocked)
          .map((entry) => {
            const fileName = `diary_${entry.date}.${extension}`;
            const content =
              format === 'html'
                ? createHtmlContent(
                    `Diary - ${format === 'html' ? entry.date : entry.date}`,
                    entry.content,
                    entry.date,
                    entry.labels || [],
                    'diary'
                  )
                : createTextContent(
                    `Diary Entry`,
                    entry.content,
                    entry.date,
                    entry.labels || [],
                    'diary'
                  );
            return { name: fileName, content };
          });
      } else if (type === 'journals') {
        setProgress(10);
        const journals = await getUserJournals(currentUser.uid, passkey);
        setProgress(50);

        files = journals
          .filter(j => !j.isLocked)
          .map((journal, index) => {
            const date = journal.updatedAt?.toDate
              ? format(journal.updatedAt.toDate(), 'yyyy-MM-dd')
              : 'unknown';
            const safeTitle = journal.title.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
            const fileName = `journal_${date}_${safeTitle}.${extension}`;
            const content =
              format === 'html'
                ? createHtmlContent(
                    journal.title,
                    journal.content,
                    journal.updatedAt?.toDate
                      ? format(journal.updatedAt.toDate(), 'MMMM d, yyyy h:mm a')
                      : 'Unknown date',
                    journal.labels || [],
                    'journal'
                  )
                : createTextContent(
                    journal.title,
                    journal.content,
                    journal.updatedAt?.toDate
                      ? format(journal.updatedAt.toDate(), 'MMMM d, yyyy h:mm a')
                      : 'Unknown date',
                    journal.labels || [],
                    'journal'
                  );
            return { name: fileName, content };
          });
      }

      setProgress(75);

      if (files.length === 0) {
        setError(`No ${type} found to export.`);
        setLoading(null);
        return;
      }

      // Create and download the file
      const blob = await createZipBlob(files);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mythoughts_${type}_${format(new Date(), 'yyyy-MM-dd')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setProgress(100);
      setSuccess(`Successfully exported ${files.length} ${type}!`);
    } catch (err: any) {
      console.error('Export error:', err);
      setError(`Failed to export ${type}: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(null);
      setProgress(0);
    }
  };

  const exportCards = [
    {
      type: 'thoughts' as const,
      title: 'All Thoughts',
      description: 'Export all your quick notes and ideas',
      icon: <ThoughtsIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      color: '#6366F1',
    },
    {
      type: 'diary' as const,
      title: 'All Diary Entries',
      description: 'Export all your daily diary entries',
      icon: <DiaryIcon sx={{ fontSize: 48, color: '#EC4899' }} />,
      color: '#EC4899',
    },
    {
      type: 'journals' as const,
      title: 'All Journals',
      description: 'Export all your journal entries',
      icon: <JournalIcon sx={{ fontSize: 48, color: '#8B5CF6' }} />,
      color: '#8B5CF6',
    },
  ];

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} showMenuButton={isMobile} />
      <Box sx={{ display: 'flex', mt: '64px' }}>
        <Sidebar
          open={sidebarOpen}
          selectedSection="download"
          onSectionSelect={(section) => {
            if (section === 'thoughts') navigate('/');
            else if (section === 'diary') navigate('/diary');
            else if (section === 'journals') navigate('/journals');
            else if (section === 'download') navigate('/download');
          }}
          onClose={() => setSidebarOpen(false)}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: '100%',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ mb: 2 }}
            >
              Back to Home
            </Button>
          </Box>

          <Paper elevation={0} sx={{ p: 4, mb: 4, bgcolor: 'background.paper', borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ZipIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                  Download Your Data
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Export all your thoughts, diary entries, and journals
                </Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Your data will be exported as a text file containing all entries. 
                Locked entries (encrypted with a different passkey) will be skipped.
              </Typography>
            </Alert>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {loading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Exporting {loading}...
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          <Grid container spacing={3}>
            {exportCards.map((card) => (
              <Grid item xs={12} md={4} key={card.type}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderTop: `4px solid ${card.color}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                    {card.icon}
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadAsZip(card.type, 'html')}
                      disabled={loading !== null}
                      sx={{ bgcolor: card.color, '&:hover': { bgcolor: card.color, filter: 'brightness(0.9)' } }}
                    >
                      Download as HTML
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadAsZip(card.type, 'text')}
                      disabled={loading !== null}
                      sx={{ borderColor: card.color, color: card.color }}
                    >
                      Download as Text
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Download;

